import { useCallback, useEffect, useRef, useState } from "react";
import type {
  ApolloError,
  LazyQueryExecFunction,
  OperationVariables,
} from "@apollo/client";

const stableStringify = (value: unknown): string => {
  if (value === null || typeof value !== "object") {
    return JSON.stringify(value);
  }

  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(",")}]`;
  }

  const record = value as Record<string, unknown>;
  return `{${Object.keys(record)
    .sort()
    .map((key) => `${JSON.stringify(key)}:${stableStringify(record[key])}`)
    .join(",")}}`;
};

export const getDefaultNodeKey = <TNode>(node: TNode): string | null => {
  if (!node || typeof node !== "object") {
    return null;
  }

  const id = (node as { id?: unknown }).id;
  if (typeof id !== "string" || !id) {
    return null;
  }

  return id;
};

export const collectUniqueNodes = <TNode>(
  nodes: TNode[],
  seenNodeKeys: Set<string>,
  getNodeKey: (node: TNode) => string | null
): TNode[] => {
  const uniqueNodes: TNode[] = [];

  for (const node of nodes) {
    const nodeKey = getNodeKey(node);

    if (!nodeKey) {
      uniqueNodes.push(node);
      continue;
    }

    if (seenNodeKeys.has(nodeKey)) {
      continue;
    }

    seenNodeKeys.add(nodeKey);
    uniqueNodes.push(node);
  }

  return uniqueNodes;
};

type ConnectionSelector<TData, TNode> = (data: TData) =>
  | {
      nodes?: (TNode | null)[] | null;
      pageInfo?: {
        hasNextPage: boolean;
        endCursor?: string | null;
      } | null;
    }
  | null
  | undefined;

export async function fetchAllPages<
  TData,
  TNode,
  TVariables extends OperationVariables,
>(
  queryFn: LazyQueryExecFunction<TData, TVariables>,
  getConnection: ConnectionSelector<TData, TNode>,
  options: {
    fetchPolicy?: "cache-first" | "network-only" | "cache-and-network";
    getNodeKey?: (node: TNode) => string | null;
    variables?: TVariables & { after?: string | null };
    getVariables?: (after: string | undefined) => TVariables;
  }
): Promise<TNode[]> {
  const accumulated: TNode[] = [];
  const seenNodeKeys = new Set<string>();
  const getNodeKey = options.getNodeKey ?? getDefaultNodeKey<TNode>;
  let after: string | undefined;
  let prevCursor: string | undefined;
  let hasNextPage = true;

  while (hasNextPage) {
    let variables = options.getVariables?.(after);
    if (!variables) {
      const baseVariables = options.variables;
      if (!baseVariables) {
        throw new Error("fetchAllPages requires variables or getVariables.");
      }
      variables = {
        ...baseVariables,
        after: after ?? null,
      };
    }

    const { data: pageData, error: pageError } = await queryFn({
      variables,
      fetchPolicy: options.fetchPolicy ?? "cache-first",
    });

    if (pageError) {
      throw pageError;
    }
    if (!pageData) {
      hasNextPage = false;
      continue;
    }

    const connection = getConnection(pageData);
    const nodes = (connection?.nodes?.filter((node) => node != null) ??
      []) as TNode[];
    accumulated.push(...collectUniqueNodes(nodes, seenNodeKeys, getNodeKey));

    const nextCursor = connection?.pageInfo?.endCursor ?? undefined;
    if (
      !connection?.pageInfo?.hasNextPage ||
      !nextCursor ||
      nextCursor === prevCursor
    ) {
      hasNextPage = false;
      continue;
    }
    prevCursor = nextCursor;
    after = nextCursor;
  }

  return accumulated;
}

/**
 * Generic hook that paginates through ALL pages of a cursor-based connection.
 *
 * Usage:
 *   const [fetch] = useOrganisationsLazyQuery();
 *   const { data, loading } = useFetchAll(fetch, (d) => d.organisations, {
 *     variables: { after: null },
 *   });
 */
export function useFetchAll<
  TData,
  TNode,
  TVariables extends OperationVariables,
>(
  queryFn: LazyQueryExecFunction<TData, TVariables>,
  getConnection: ConnectionSelector<TData, TNode>,
  options?: {
    skip?: boolean;
    fetchPolicy?: "cache-first" | "network-only" | "cache-and-network";
    getNodeKey?: (node: TNode) => string | null;
    variables?: TVariables & { after?: string | null };
    getVariables?: (after: string | undefined) => TVariables;
  }
): {
  data: TNode[];
  loading: boolean;
  error?: ApolloError;
  refetch: () => Promise<void>;
} {
  const [data, setData] = useState<TNode[]>([]);
  const [loading, setLoading] = useState(!options?.skip);
  const [error, setError] = useState<ApolloError | undefined>();
  const mountedRef = useRef(true);

  // Store in refs so inline arrows don't cause infinite re-fetches
  const getConnectionRef = useRef(getConnection);
  getConnectionRef.current = getConnection;
  const fetchPolicyRef = useRef(options?.fetchPolicy);
  fetchPolicyRef.current = options?.fetchPolicy;
  const getNodeKeyRef = useRef(options?.getNodeKey);
  getNodeKeyRef.current = options?.getNodeKey;
  const variablesRef = useRef(options?.variables);
  variablesRef.current = options?.variables;
  const getVariablesRef = useRef(options?.getVariables);
  getVariablesRef.current = options?.getVariables;
  const variablesKey = stableStringify(
    options?.getVariables?.(undefined) ?? options?.variables ?? null
  );

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(undefined);

    try {
      const accumulated = await fetchAllPages(
        queryFn,
        getConnectionRef.current,
        {
          fetchPolicy: fetchPolicyRef.current,
          getNodeKey: getNodeKeyRef.current,
          variables: variablesRef.current,
          getVariables: getVariablesRef.current,
        }
      );

      if (mountedRef.current) {
        setData(accumulated);
      }
    } catch (e) {
      if (mountedRef.current) {
        setError(e as ApolloError);
      }
    }

    if (mountedRef.current) {
      setLoading(false);
    }
  }, [queryFn]);

  const skip = options?.skip ?? false;

  useEffect(() => {
    mountedRef.current = true;
    if (!skip) {
      void fetchAll();
    }
    return () => {
      mountedRef.current = false;
    };
  }, [fetchAll, skip, variablesKey]);

  return { data, loading, error, refetch: fetchAll };
}
